import { Link } from "react-router-dom";
import { Clock, Pill, Pencil, UserCheck, CalendarClock } from "lucide-react";
import FeedbackBadge from "./FeedbackBadge";
import AdHocBadge from "./AdHocBadge";
import OffListBadge from "./OffListBadge";
import CategoryBadge from "../doctors/CategoryBadge";
import { formatISTDate, formatISTTime, todayIST } from "../../lib/datetime";
import { isOverdue, joinParts } from "../../lib/vocabulary";

export default function VisitCard({ visit, showOfficer = false }) {
  const productLine = joinParts((visit.products_discussed || []).slice(0, 3));
  const extraCount = Math.max(0, (visit.products_discussed || []).length - 3);

  // RC-11: `follow_up_date` was written by the form and read back by nothing.
  // A rep scanning the list could not see that one was due at all, let alone
  // that it had passed.
  const followUp = visit.follow_up_required ? visit.follow_up_date : null;
  const overdue = isOverdue(followUp, todayIST());

  return (
    <Link
      to={`/doctor-visits/${visit.id}`}
      data-testid={`visit-card-${visit.id}`}
      className="block bg-white border border-callisto rounded-xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-callisto-navy leading-tight truncate">
              {visit.doctor_name}
            </h3>
            {visit.doctor_category && <CategoryBadge category={visit.doctor_category} size="sm" />}
            <AdHocBadge visit={visit} />
            <OffListBadge visit={visit} />
          </div>
          <p className="mt-0.5 text-xs text-callisto-muted flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <Clock size={12} />
              {formatISTTime(visit.submitted_at)}
            </span>
            <span>·</span>
            <span>{formatISTDate(visit.visit_date)}</span>
            {visit.is_edited && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-0.5 text-callisto-gold">
                  <Pencil size={11} />
                  edited
                </span>
              </>
            )}
          </p>
        </div>
        <FeedbackBadge feedback={visit.doctor_feedback} />
      </div>

      {productLine && (
        <div className="mt-3 flex items-start gap-1.5 text-xs text-callisto-body">
          <Pill size={13} className="shrink-0 text-callisto-navy/60" />
          <span className="truncate">
            {productLine}
            {extraCount > 0 && (
              <span className="text-callisto-muted"> · +{extraCount} more</span>
            )}
          </span>
        </div>
      )}

      {followUp && (
        <div
          className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold ${
            overdue ? "text-red-700" : "text-callisto-muted"
          }`}
          data-testid={`visit-followup-${visit.id}`}
          data-overdue={overdue ? "true" : "false"}
        >
          <CalendarClock size={12} className="shrink-0" />
          {overdue
            ? `Follow-up overdue — was due ${formatISTDate(followUp)}`
            : `Follow-up due ${formatISTDate(followUp)}`}
        </div>
      )}

      {showOfficer && visit.sales_officer_name && (
        <div className="mt-3 pt-3 border-t border-callisto flex items-center gap-1.5 text-[11px] text-callisto-muted">
          <UserCheck size={12} />
          <span>by</span>
          <span className="font-semibold text-callisto-navy truncate">
            {visit.sales_officer_name}
          </span>
        </div>
      )}
    </Link>
  );
}
